'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { MdBookmarkAdd, MdClose, MdDelete, MdFilterList } from 'react-icons/md'
import { toast } from 'react-toastify'
import MultiSelectFilter from '@/components/multi-select-filter'
import ConfirmModal from '@/components/confirm-modal'
import { getOrganizationUsers, OrganizationUser } from '@/lib/api/users/users-api'
import { getAttendantsByOrganization } from '@/lib/api/atendimento/attendants'
import { getPipelines, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import {
    ChatFilterPreset,
    createChatFilterPreset,
    deleteChatFilterPreset,
    getChatFilterPresets,
} from '@/lib/api/atendimento/chat-filter-presets'
import {
    ChatFilters,
    PANEL_KEYS,
    countActiveFilters,
} from './chat-filters'

const CHANNEL_OPTIONS = [
    { value: 'WHATSAPP', label: 'WhatsApp' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'MESSENGER', label: 'Messenger' },
    { value: 'DEX_TALK', label: 'Dex' },
]

const SENDER_OPTIONS = [
    { value: 'CLIENT', label: 'Contato' },
    { value: 'TEAM', label: 'Equipe' },
    { value: 'AI', label: 'Agente de IA' },
    { value: 'AUTOMATION', label: 'Automação (fluxo, lembrete)' },
]

const INTERACTION_OPTIONS = [
    { value: 'INBOUND', label: 'Contato falou por último' },
    { value: 'OUTBOUND_UNANSWERED', label: 'Falamos e ninguém respondeu' },
    { value: 'OUTBOUND_ANSWERED', label: 'Respondemos o contato' },
]

const DATE_FIELD_OPTIONS = [
    { value: 'lastMessageAt', label: 'Última mensagem' },
    { value: 'createdAt', label: 'Início da conversa' },
]

interface ChatFilterPanelProps {
    filters: ChatFilters
    onChange: (patch: Partial<ChatFilters>) => void
    onApplyPreset: (filters: ChatFilters) => void
    onClear: () => void
    organizationId?: number | null
}

/**
 * Painel de filtros da lista de atendimentos.
 *
 * Fica num popover, e não fixo acima da lista, porque a coluna de conversas é
 * estreita: doze campos em linha comprimiriam a lista, que é o que o operador olha
 * o tempo todo. O badge no botão mostra quantos filtros estão ativos, para o
 * recorte nunca ficar invisível.
 */
export default function ChatFilterPanel({
    filters,
    onChange,
    onApplyPreset,
    onClear,
    organizationId,
}: ChatFilterPanelProps) {
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const [users, setUsers] = useState<OrganizationUser[]>([])
    const [attendants, setAttendants] = useState<{ id: number; name: string }[]>([])
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])

    const [presets, setPresets] = useState<ChatFilterPreset[]>([])
    const [savingPreset, setSavingPreset] = useState(false)
    const [presetName, setPresetName] = useState('')
    const [showSaveForm, setShowSaveForm] = useState(false)
    const [presetToDelete, setPresetToDelete] = useState<ChatFilterPreset | null>(null)

    const [tagInput, setTagInput] = useState('')

    const activeCount = countActiveFilters(filters)

    useEffect(() => {
        getChatFilterPresets()
            .then(res => {
                if (res.success && Array.isArray(res.result)) setPresets(res.result)
            })
            .catch(() => {
                // Sem presets a tela funciona igual: o painel continua servindo.
            })
    }, [])

    // As listas de apoio só carregam quando o painel abre pela primeira vez — são
    // três chamadas que a maioria das sessões nunca precisa.
    useEffect(() => {
        if (!open || users.length > 0 || pipelines.length > 0) return

        getOrganizationUsers()
            .then(res => {
                if (res.success && Array.isArray(res.result)) setUsers(res.result)
            })
            .catch(() => {})

        getPipelines()
            .then(res => {
                if (res.success && Array.isArray(res.result)) setPipelines(res.result)
            })
            .catch(() => {})

        if (organizationId) {
            getAttendantsByOrganization(organizationId)
                .then(res => {
                    if (res.success && Array.isArray(res.result)) {
                        setAttendants(res.result.map(a => ({ id: a.id, name: a.name })))
                    }
                })
                .catch(() => {})
        }
    }, [open, organizationId, users.length, pipelines.length])

    // Fecha ao clicar fora, como os demais popovers da tela. O menu dos campos de
    // escolha múltipla é renderizado em portal (fora deste container) para não ser
    // recortado pela rolagem: clicar numa opção não pode fechar o painel.
    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null
            if (target?.closest?.('[data-portal-menu]')) return
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', onPointerDown)
        return () => document.removeEventListener('pointerdown', onPointerDown)
    }, [open])

    const userOptions = useMemo(
        () => users.map(user => ({ value: String(user.id), label: user.name })),
        [users],
    )

    const pipelineOptions = useMemo(
        () => pipelines.map(pipeline => ({ value: String(pipeline.id), label: pipeline.name })),
        [pipelines],
    )

    // Só as etapas dos funis escolhidos: a lista inteira da organização passa de
    // cinquenta itens, e a maioria não tem relação com o funil que se está olhando.
    const stageOptions = useMemo(() => {
        const selected = filters.pipelineId ?? []
        const visible = selected.length
            ? pipelines.filter(pipeline => selected.includes(pipeline.id))
            : pipelines
        return visible.flatMap(pipeline =>
            (pipeline.stages ?? []).map(stage => ({
                value: String(stage.id),
                label: selected.length === 1 ? stage.name : `${pipeline.name} · ${stage.name}`,
            })),
        )
    }, [pipelines, filters.pipelineId])

    const toNumbers = (values: string[] | undefined) =>
        values?.length ? values.map(Number).filter(Number.isInteger) : undefined

    const addTag = () => {
        const tag = tagInput.trim()
        if (!tag) return
        const current = filters.tags ?? []
        if (!current.includes(tag)) onChange({ tags: [...current, tag] })
        setTagInput('')
    }

    const savePreset = async () => {
        const name = presetName.trim()
        if (!name) return
        setSavingPreset(true)
        try {
            // Guarda o recorte inteiro da tela, não só o painel: quem salva
            // "Sem resposta" espera que o status e o setor voltem junto.
            const res = await createChatFilterPreset({
                name,
                filters,
                sortOrder: presets.length,
            })
            if (res.success && res.result) {
                setPresets(prev => [...prev, res.result as ChatFilterPreset])
                setPresetName('')
                setShowSaveForm(false)
                toast.success('Filtro salvo')
            }
        } catch {
            toast.error('Não foi possível salvar o filtro')
        } finally {
            setSavingPreset(false)
        }
    }

    const removePreset = async () => {
        if (!presetToDelete) return
        try {
            const res = await deleteChatFilterPreset(presetToDelete.id)
            if (res.success) {
                setPresets(prev => prev.filter(preset => preset.id !== presetToDelete.id))
            }
        } catch {
            toast.error('Não foi possível excluir o filtro')
        } finally {
            setPresetToDelete(null)
        }
    }

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                className={`btn btn-sm gap-2 ${activeCount > 0 ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                title="Filtros"
            >
                <MdFilterList size={18} />
                <span className="hidden sm:inline">Filtros</span>
                {activeCount > 0 && <span className="badge badge-sm">{activeCount}</span>}
            </button>

            {open && (
                <div className="absolute left-0 z-40 mt-2 w-[min(38rem,calc(100vw-2rem))] rounded-2xl border border-base-300 bg-base-100 p-4 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Filtrar conversas</h3>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle"
                            onClick={() => setOpen(false)}
                            aria-label="Fechar filtros"
                        >
                            <MdClose size={16} />
                        </button>
                    </div>

                    {presets.length > 0 && (
                        <div className="mb-4">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-base-content/60">
                                Filtros salvos
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {presets.map(preset => (
                                    <span key={preset.id} className="join">
                                        <button
                                            type="button"
                                            className="btn btn-xs join-item"
                                            onClick={() => {
                                                onApplyPreset(preset.filters || {})
                                                setOpen(false)
                                            }}
                                        >
                                            {preset.name}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-xs join-item"
                                            onClick={() => setPresetToDelete(preset)}
                                            aria-label={`Excluir ${preset.name}`}
                                        >
                                            <MdDelete size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid max-h-[60vh] grid-cols-1 gap-x-4 gap-y-1 overflow-y-auto overflow-x-hidden pr-1 sm:grid-cols-2">
                        <MultiSelectFilter
                            label="Status de interação"
                            options={INTERACTION_OPTIONS}
                            value={filters.interaction}
                            onChange={values =>
                                onChange({ interaction: values as ChatFilters['interaction'] })
                            }
                        />

                        <MultiSelectFilter
                            label="Remetente da última mensagem"
                            options={SENDER_OPTIONS}
                            value={filters.lastSender}
                            onChange={values =>
                                onChange({ lastSender: values as ChatFilters['lastSender'] })
                            }
                        />

                        <div className="form-control w-full min-w-0">
                            <label className="label">
                                <span className="label-text">Responsável pelo contato</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={filters.responsibleUserId ?? ''}
                                onChange={event =>
                                    onChange({
                                        responsibleUserId: event.target.value
                                            ? Number(event.target.value)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {userOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full min-w-0">
                            <label className="label">
                                <span className="label-text">Quem respondeu por último</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={filters.lastMessageUserId ?? ''}
                                onChange={event =>
                                    onChange({
                                        lastMessageUserId: event.target.value
                                            ? Number(event.target.value)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">Qualquer pessoa</option>
                                {userOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <MultiSelectFilter
                            label="Canal"
                            options={CHANNEL_OPTIONS}
                            value={filters.channel}
                            onChange={values => onChange({ channel: values })}
                            menuMinWidth={240}
                        />

                        <div className="form-control w-full min-w-0">
                            <label className="label">
                                <span className="label-text">Agente de IA</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={filters.attendantId ?? ''}
                                onChange={event =>
                                    onChange({
                                        attendantId: event.target.value
                                            ? Number(event.target.value)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {attendants.map(attendant => (
                                    <option key={attendant.id} value={attendant.id}>
                                        {attendant.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <MultiSelectFilter
                            label="Funil"
                            options={pipelineOptions}
                            value={filters.pipelineId?.map(String)}
                            onChange={values =>
                                onChange({ pipelineId: toNumbers(values), stageId: undefined })
                            }
                            searchable
                            menuMinWidth={320}
                        />

                        {/* A etapa ocupa a linha inteira: "(VDC1) CONSULTAS E EXAMES · Aguardando
                            atendimento" não cabe em meia coluna, e escolher para onde mover sem
                            ler o nome inteiro é adivinhação. */}
                        <div className="min-w-0 sm:col-span-2">
                            <MultiSelectFilter
                                label="Etapa"
                                options={stageOptions}
                                value={filters.stageId?.map(String)}
                                onChange={values => onChange({ stageId: toNumbers(values) })}
                                searchable
                                menuMinWidth={420}
                            />
                        </div>

                        <div className="form-control w-full min-w-0">
                            <label className="label">
                                <span className="label-text">Período por</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={filters.dateField ?? 'lastMessageAt'}
                                onChange={event =>
                                    onChange({
                                        dateField: event.target.value as ChatFilters['dateField'],
                                    })
                                }
                            >
                                {DATE_FIELD_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full min-w-0">
                            <label className="label">
                                <span className="label-text">De / até</span>
                            </label>
                            <div className="flex min-w-0 items-center gap-2">
                                <input
                                    type="date"
                                    className="input input-bordered w-full min-w-0"
                                    value={filters.dateFrom?.slice(0, 10) ?? ''}
                                    onChange={event =>
                                        onChange({ dateFrom: event.target.value || undefined })
                                    }
                                />
                                <input
                                    type="date"
                                    className="input input-bordered w-full min-w-0"
                                    value={filters.dateTo?.slice(0, 10) ?? ''}
                                    onChange={event =>
                                        onChange({ dateTo: event.target.value || undefined })
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full min-w-0 sm:col-span-2">
                            <label className="label">
                                <span className="label-text">Tags do lead ou da oportunidade</span>
                            </label>
                            <div className="flex min-w-0 items-center gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered w-full min-w-0"
                                    placeholder="Digite a tag e pressione Enter"
                                    value={tagInput}
                                    onChange={event => setTagInput(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault()
                                            addTag()
                                        }
                                    }}
                                />
                                <button type="button" className="btn btn-sm shrink-0" onClick={addTag}>
                                    Adicionar
                                </button>
                            </div>
                            {(filters.tags ?? []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {(filters.tags ?? []).map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            className="badge badge-outline gap-1"
                                            onClick={() =>
                                                onChange({
                                                    tags: (filters.tags ?? []).filter(
                                                        item => item !== tag,
                                                    ),
                                                })
                                            }
                                        >
                                            {tag}
                                            <MdClose size={12} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="form-control w-full min-w-0 sm:col-span-2">
                            <div className="flex flex-wrap gap-4 pt-2">
                                <label className="label cursor-pointer gap-2">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm"
                                        checked={filters.unanswered === true}
                                        onChange={event =>
                                            onChange({
                                                unanswered: event.target.checked ? true : undefined,
                                            })
                                        }
                                    />
                                    <span className="label-text">Sem resposta</span>
                                </label>
                                <label className="label cursor-pointer gap-2">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm"
                                        checked={filters.unread === true}
                                        onChange={event =>
                                            onChange({
                                                unread: event.target.checked ? true : undefined,
                                            })
                                        }
                                    />
                                    <span className="label-text">Não lidas</span>
                                </label>
                                <label className="label cursor-pointer gap-2">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm"
                                        checked={filters.waitingForHuman === true}
                                        onChange={event =>
                                            onChange({
                                                waitingForHuman: event.target.checked
                                                    ? true
                                                    : undefined,
                                            })
                                        }
                                    />
                                    <span className="label-text">Aguardando atendimento humano</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-base-300 pt-3">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={onClear}
                            disabled={activeCount === 0}
                        >
                            Limpar filtros
                        </button>

                        {showSaveForm ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-44"
                                    placeholder="Nome do filtro"
                                    value={presetName}
                                    onChange={event => setPresetName(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') savePreset()
                                    }}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={savePreset}
                                    disabled={savingPreset || !presetName.trim()}
                                >
                                    Salvar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setShowSaveForm(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm gap-2"
                                onClick={() => setShowSaveForm(true)}
                                disabled={activeCount === 0}
                            >
                                <MdBookmarkAdd size={16} />
                                Salvar este filtro
                            </button>
                        )}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={!!presetToDelete}
                title="Excluir filtro salvo"
                message={`Excluir o filtro "${presetToDelete?.name ?? ''}"?`}
                confirmButtonText="Excluir"
                onConfirm={removePreset}
                onClose={() => setPresetToDelete(null)}
            />
        </div>
    )
}

export { PANEL_KEYS }
